// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from "react-redux";
import compose from 'recompose/compose';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { withStyles, Link } from '@material-ui/core';
import WarningIcon from "@material-ui/icons/Warning";
import { arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import { cloneDeep, difference, findIndex, each, keys, map, size } from 'lodash';

import ListContainer from "../../../list";
import {
  GRID_ROWS_PER_PAGE_DEFAULT_OPTION_24,
  GRID_ROWS_PER_PAGE_EXTRA_OPTIONS,
  ASSIGNED,
  NOT_ASSIGNED,
  ALERT_SUCCESS,
  ANY,
} from "../../../../constants";
import { getI18n, getI18nFormattedMessage } from "../../../../utils/intl";
import {
  FilterByName,
  FilterByOrgs,
  FilterByStatus,
  FilterSeparator,
  AddButton,
  HeaderActionButtons,
} from './controls';
import {
  TiersSelect as TiersSelectDialog,
} from '../../../../pageComponents';
import {
  Switch,
  ToolTip as ToolTipComp,
  FormActionButtons,
  AlertDialog,
  FormSelect,
} from '../../../../components';
import { getApplicationStatusColor, getApplicationStatusLabel } from '../../../../utils/deployment';
import { hasCRUDPermissions } from '../../../../utils';
import {
  PERMISSION_APPLICATIONS_PRODUCT,
  CHANGE_MODE_REMOVE_ACCESS,
  CHANGE_MODE_ADD_ACCESS,
  APPLICATION_LIST_STATUS_BY,
  PERMISSION_APPLICATION,
  APPLICATION_STATUS_PENDING_APPROVAL,
  APPLICATION_STATUS_REJECTED,
  APPLICATION_STATUS_DELETE_FAILED,
  PERMISSION_ORGANIZATION,
  PERMISSION_TIERS_APPLICATION,
} from '../../../../constants';
import {
  fetchApplications,
  updateProductApps,
  fetchTiersProduct,
} from "../../../../actions/products";
import {
  getAppsListResults,
  getAppsListTotalElements,
  getAppsListTotalPages,
  getIsAppUpdateSuccess,
  getTiersProduct,
  getproductDetails,
} from "../../../../reducers/products";
import { FormDialog } from '../../../../components';
import EulaDialog from '../../../../pageComponents/EulaDialog';
import {
  getFeatureFlagForDistributeRateLimit,
} from '../../../../reducers/portalConfig';
import { hasOrgBoundRole } from '../../../../utils';
import styles from './styles';
import { fetchApiEula } from '../../../../actions/application';
import { getApiEula } from '../../../../reducers/application';

const getDefaultTier = (tiersList) => tiersList.find(({ isDefaultTier }) => isDefaultTier);
const USE_ORG_DEFAULT = 'Use Org Default';

export const Applications = (props) => {
  const {
    classes,
    productUuid,
    isLoading,
    rows = [],
    totalElements,
    totalPages,
    isCurrentTab,
    isAppUpdateSuccess,
    notifyMessages,
    userContext,
    tiersListArray,
    tabValue,
    productDetails,
    eulaDetails,
    fetchApiEula,
  } = props;

  const hasRead = hasCRUDPermissions(userContext, ['READ'], PERMISSION_APPLICATIONS_PRODUCT);
  const hasUpdate = hasCRUDPermissions(userContext, ['UPDATE'], PERMISSION_APPLICATIONS_PRODUCT);
  const hasReadApp = hasCRUDPermissions(userContext, ['READ'], PERMISSION_APPLICATION);
  const hasReadOrg = hasCRUDPermissions(userContext, ['READ'], PERMISSION_ORGANIZATION);
  const hasUpdateTier = hasCRUDPermissions(userContext, ['UPDATE'],PERMISSION_TIERS_APPLICATION);
  const hasOrgUser = hasOrgBoundRole(userContext);
  const disabledAppStatusArray= [
    APPLICATION_STATUS_DELETE_FAILED,
    APPLICATION_STATUS_PENDING_APPROVAL,
    APPLICATION_STATUS_REJECTED,
  ]

  useEffect(() => {
    if (isCurrentTab) {
      if(!hasRead) {
        props.push('/404');
      }
    }
  }, [isCurrentTab, userContext]);

  const [filterByName, setFilterByName] = useState('');
  const [filterByOrg, setFilterByOrg] = useState('');
  const [isOpenTier, setIsOpenTier] = useState(false);
  const [changeMode, setChangeMode] = useState(CHANGE_MODE_REMOVE_ACCESS);
  const [filterByStatus, setFilterByStatus] = useState(ANY);
  const [showFormActionBtns, setShowFormActionBtns] = useState(true);
  const [currentAppsRows, setCurrentAppsRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRowsObject, setSelectedRowsObject] = useState([]);
  const [modifiedRecords, setModifiedRecords] = useState(0);
  const [showTierSelectionDialog, setShowTierSelectionDialog] = useState(false);
  const [selectedTier, setSelectedTier] = useState('');
  const [selectedTierDialog, setSelectedTierDialog] = useState('');
  const [selectedTierArray, setSelectedTierArray] = useState([]);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [filterByNameApplied, setFilterByNameApplied] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION_24);
  const [page, setPage] = useState(0);
  const [showEulaDialog, setShowEulaDialog] = useState(false);
  const [eulaDetailsMap, setEulaDetailsMap] = useState(null);
  const [eulaProductMap, setEulaProductMap] = useState(null);
  const [showBulkTier, setShowBulkTier] = useState(false);
  const [selectBulkTier, setSelectBulkTier] = useState(null);
  const [tiersList, setTiersList] = useState([]);

  const intl = getI18n(useIntl());
  const statusData = APPLICATION_LIST_STATUS_BY.filter(status =>
    status.uuid !== APPLICATION_STATUS_REJECTED)
  const statusDataInChangeMode = APPLICATION_LIST_STATUS_BY.filter(status =>
    status.uuid !== APPLICATION_STATUS_REJECTED && status.uuid !== APPLICATION_STATUS_DELETE_FAILED)
  const fetchListWithFilters = ({
    hasAccess = changeMode === CHANGE_MODE_REMOVE_ACCESS ? ASSIGNED : NOT_ASSIGNED,
    name = filterByName,
    pageNum = page,
    rowsPerPageArg = rowsPerPage,
    state = filterByStatus,
    orgName = filterByOrg,
  } = { }) => {
    props.fetchApplications({
      uuid: productUuid,
      name : name,
      assign: hasAccess,
      page: pageNum,
      size: rowsPerPageArg,
      status: state,
      orgName: orgName,
    });
  };

  useEffect(() => {
    if (productUuid && isCurrentTab) {
      props.fetchTiersProduct(productUuid, ASSIGNED);
      fetchListWithFilters();
      setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
    }
  }, [productUuid, isCurrentTab]);

  useEffect(() => {
    setShowFormActionBtns(true);
    if ((changeMode === CHANGE_MODE_ADD_ACCESS) || isFormDirty) {
      setShowFormActionBtns(true);
    }
    if ((changeMode !== CHANGE_MODE_ADD_ACCESS) && !isFormDirty) {
      setShowFormActionBtns(false);
    }
  }, [changeMode, isFormDirty]);

  useEffect(() => {
    if(tabValue === 'products-details-applications-tab') {
      fetchListWithFilters();
    }
  }, [changeMode]);

  useEffect(() => {
    if(productDetails && productDetails.eulaUuid){
      setEulaProductMap({
        [productDetails.eulaUuid]: [{
          ...productDetails,
          productName: productDetails.name,
        }],
      });
    }
    return () => {
      setEulaProductMap({});
    };
  },[productDetails])

  useEffect(() => {
    if(eulaDetails){
      setEulaDetailsMap({
        [eulaDetails.uuid]: eulaDetails,
      });
    }
    return () => {
      setEulaDetailsMap({});
    }
  },[eulaDetails])

  useEffect(() => {
    const rowsMap = {};
    each(rows, item => {
      rowsMap[item.uuid] = item;
    });
    const currentAppsRowsMap = {};
    each(currentAppsRows, item => {
      currentAppsRowsMap[item.uuid] = item;
    });
    const rowIds = keys(rowsMap);
    const currentAppsRowsIds = keys(currentAppsRowsMap);
    if ( difference(rowIds, currentAppsRowsIds).length !== 0) {
      return ;
    }
    let modifiedRecords = 0;
    each(rowIds, id => {
    if (rowsMap[id].appProductAccessStatus !==
      currentAppsRowsMap[id].appProductAccessStatus ||
      rowsMap[id].tierUuid !== currentAppsRowsMap[id].tierUuid) {
        modifiedRecords++;
    }
    });
    setModifiedRecords(modifiedRecords);
  }, [currentAppsRows]);

  useEffect(() => {
      setCurrentAppsRows(cloneDeep(rows));
  }, [rows]);

  useEffect(() => {
    if (tiersListArray.length > 0 && isCurrentTab) {
      const listTier = [{ uuid: 0, name: USE_ORG_DEFAULT, tierName: USE_ORG_DEFAULT },
        ...tiersListArray];
      setTiersList(listTier);
      setSelectedTier(0);
      setSelectedTierDialog(0);
    }
  }, [tiersListArray]);

  useEffect(() => {
    (modifiedRecords > 0) ? setIsFormDirty(true) : setIsFormDirty(false);
  }, [modifiedRecords]);

  useEffect(() => {
    if (isAppUpdateSuccess) {
      clearDisable();
      notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.product.updated'));
      setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
      setPage(0);
      fetchListWithFilters({ pageNum: 0 });
      setTimeout(() => { notifyMessages('', '')}, 2500);
    }
  }, [isAppUpdateSuccess]);

  const onClickTier = (uuid) => {
    setSelectedRows([uuid]);
    setShowTierSelectionDialog(true);
  }

  const onBulkTierSelect = () => {
    setShowBulkTier(true);
  }

  const onCloseTier = () => setIsOpenTier(false);

  const clearDisable = () => {
    setSelectedRows([]);
    setCurrentAppsRows([]);
    setModifiedRecords(0);
    setFilterByName('');
    setFilterByOrg('');
    setFilterByStatus(ANY);
    setFilterByNameApplied(false);
    setPage(0);
  }

  const onFilterByNameChange = (value) => {
    setFilterByName(value);
  };

  const onFilterByNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      setFilterByNameApplied(true);
      setPage(0);
      fetchListWithFilters({ pageNum: 0 });
    }
  };

  const onFilterByOrgChange = (value) => {
    setFilterByOrg(value);
  };

  const onFilterByOrgKeyPress = (e) => {
    if (e.key === 'Enter') {
      setFilterByNameApplied(true);
      setPage(0);
      fetchListWithFilters({ pageNum: 0 });
    }
  };

  const onFilterByStatusChange = (newStatus) => {
    setFilterByStatus(newStatus);
    setPage(0);
    fetchListWithFilters({ state: newStatus, pageNum: 0 });
  };

  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    fetchListWithFilters({ pageNum: newPage });
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchListWithFilters({ pageNum: 0, rowsPerPageArg : newRowsPerPage });
  };

  const onSelectTier = (value) => {
    setSelectedTierDialog(value);
  }

  const onAddTierSubmit = () => {
    localStorage.setItem('isUnSavedChanges-products', true);
    const tier = tiersList.find(({ tierUuid }) => tierUuid === selectedTierDialog );
    currentAppsRows.filter(obj => {
      if(obj.uuid === selectedRows[0]) {
        obj.appProductAccessStatus = ASSIGNED;
        obj.tierName = tier.tierName;
        obj.tierUuid = tier.tierUuid;
        obj.isModifyTier = true;
        obj.isModify = true;
      }
    });
    setCurrentAppsRows([...currentAppsRows]);
    onCloseTier();
    setSelectedRows([]);
    setSelectedTierDialog('');
  }

  const updateCurrentAppRow = (uuid, access) => {
    const index = findIndex(currentAppsRows, item => item.uuid === uuid);
    currentAppsRows[index].appProductAccessStatus = access ? ASSIGNED : NOT_ASSIGNED;
    if(changeMode === CHANGE_MODE_REMOVE_ACCESS)
        currentAppsRows[index].isModify = access ? false : true ;
    else {
      currentAppsRows[index].isModify = access ? true : false;
      if (access) {
        if(hasUpdateTier) {
          //setting up org default tier
          if (changeMode === CHANGE_MODE_ADD_ACCESS) {
            currentAppsRows[index].tierName = USE_ORG_DEFAULT;
            currentAppsRows[index].tierUuid = null;
          } else {
            const tier = getDefaultTier(tiersList);
            currentAppsRows[index].tierName = tier.tierName;
            currentAppsRows[index].tierUuid = tier.tierUuid;
          }
        }
      } else {
        delete currentAppsRows[index].tierName;
        delete currentAppsRows[index].tierUuid;
      }
      currentAppsRows[index].isModifyTier = access ? true : false;
    }
    setCurrentAppsRows([...currentAppsRows]);
  };

  const onAccessToggle = (val, uuid) => {
    updateCurrentAppRow(uuid, !!val);
  };

  const onAddAccessTier = () => {
    localStorage.setItem('isUnSavedChanges-products', true);
    selectedRows.forEach((id) => {
      currentAppsRows.filter(obj => {
        if(obj.uuid === id) {
          obj.appProductAccessStatus = ASSIGNED;
          let tier = { tierUuid: null, tierName: USE_ORG_DEFAULT };
          if(selectedTierArray.length > 0) {
            tier = selectedTierArray.find(({ applicationUuid }) => applicationUuid === id );
          }
          obj.tierName = tier.tierName;
          obj.tierUuid = tier.tierUuid;
          //Added isModify to identify which record has changed in the appslist
          obj.isModify = true;
          //Added isChangeTier to identify tier has changed in the appslist
          obj.isModifyTier = true;
        }
      });
    });
    setCurrentAppsRows([...currentAppsRows]);
    setSelectedRows([]);
    setShowBulkTier(false);
    setSelectBulkTier(null);
  }

  const onTierClick = (tierUuid, tierName) => {
    if(selectedRows.length > 0 ) {
      const index = findIndex(currentAppsRows, item => item.uuid === selectedRows[0]);
      currentAppsRows[index].tierUuid = tierUuid;
      currentAppsRows[index].tierName = tierName;
      //Added isModify to identify which record has changed in the appslist
      currentAppsRows[index].isModify = true;
      //Added isChangeTier to identify tier has changed in the appslist
      currentAppsRows[index].isModifyTier = true;
      setCurrentAppsRows([...currentAppsRows]);
    }
    setShowTierSelectionDialog(false);
  };

  const renderProdTiers = () => {
    let activeTierUuid = '-1';
    if(selectedRows.length > 0) {
      const index = findIndex(currentAppsRows, item => item.uuid === selectedRows[0]);
      activeTierUuid = currentAppsRows[index].tierUuid;
    }
    const assignedAppTiers = map(
      tiersList, item => ({
        ... item,
        onClick: onTierClick.bind(null,item.tierUuid, item.tierName, item.uuid),
        selected: item.tierUuid === activeTierUuid,
        classes: { root: classes.tableRow },
      }),
    );

    return (<TiersSelectDialog
      activeTierUuid={activeTierUuid}
      classes={classes}
      featureFlagDistributeRateLimit={true}
      rows={assignedAppTiers}
    />);
  };

  const onBulkTierClick = (selectTierUuid) => {
    const tiersMap = [];
    selectedRows.forEach((id) => {
      const tier = tiersList.find( ({ tierUuid }) => tierUuid === selectTierUuid);
      const obj = { applicationUuid: id, tierUuid: selectTierUuid, tierName: tier.tierName };
      tiersMap.push(obj);
    });
    setSelectedTierArray(tiersMap);
    setSelectBulkTier(selectTierUuid);
  };

  const renderBulkTiers = () => {
    const assignedAppTiers = map(
      tiersList, item => ({
        ... item,
        onClick: onBulkTierClick.bind(null,item.tierUuid, item.tierName, item.uuid),
        selected: item.tierUuid === selectBulkTier,
        classes: { root: classes.tableRow },
      }),
    );

    return (<TiersSelectDialog
      activeTierUuid={selectBulkTier}
      classes={classes}
      featureFlagDistributeRateLimit={true}
      rows={assignedAppTiers}
    />);
  };

  const onSave = () => {
    localStorage.setItem('isUnSavedChanges-products', false);
    const data = [];
    const dataAccess = [];
    if(changeMode === CHANGE_MODE_REMOVE_ACCESS) {
      currentAppsRows.map((app)=> {
        if(app.isModify && !app.isModifyTier) {
          const obj = { applicationUuid: app.uuid, tierUuid: app.tierUuid };
          data.push(obj);
        }
        if(app.isModify && app.isModifyTier) {
          if(app.appProductAccessStatus === NOT_ASSIGNED) {
            const obj = { applicationUuid: app.uuid, tierUuid: app.tierUuid };
            data.push(obj);
          } else {
            const obj = { applicationUuid: app.uuid, tierUuid: app.tierUuid };
            dataAccess.push(obj);
          }
        }
      });
      if(data.length > 0 )
        props.updateProductApps(productUuid, data, 'remove');
      if(dataAccess.length > 0)
          props.updateProductApps(productUuid, dataAccess);
    } else {
      if(size(eulaProductMap) > 0){
        Object.keys(eulaProductMap).forEach(eulaUuid => fetchApiEula(eulaUuid));
        setShowEulaDialog(true);
      }else{
        onSubmitEulaDialog();
      }
    }
  }

  const onCancel = () => {
    localStorage.setItem('isUnSavedChanges-products', false);
    clearDisable();
    setShowFormActionBtns(false);
    if (changeMode === CHANGE_MODE_ADD_ACCESS) {
      setChangeMode(CHANGE_MODE_REMOVE_ACCESS);
    }else{
      fetchListWithFilters({
        hasAccess: ASSIGNED,
        name: '',
        pageNum: 0,
        rowsPerPageArg:rowsPerPage,
        state: ANY,
        orgName:'' });
    }
  }

  const onRemoveAccess = () => {
    selectedRows.forEach((id) => {
      currentAppsRows.filter(obj => {
        if(obj.uuid === id) {
          obj.appProductAccessStatus = changeMode === CHANGE_MODE_ADD_ACCESS ?
            ASSIGNED : NOT_ASSIGNED;
          obj.isModify = true;
          if(changeMode === CHANGE_MODE_ADD_ACCESS) {
            obj.tierName = obj.tierName ? obj.tierName : USE_ORG_DEFAULT;
            obj.tierUuid = obj.tierUuid ? obj.tierUuid : null;
            obj.isModifyTier = true;
          }
        }
      });
    });
    setSelectedRows([]);
    setCurrentAppsRows([...currentAppsRows]);
  }

  const onRemoveAssign = () => {
    selectedRows.forEach((id) => {
      currentAppsRows.filter(obj => {
        if(obj.uuid === id) {
          obj.appProductAccessStatus = NOT_ASSIGNED;
          obj.isModify = false;
          delete obj.tierName;
          delete obj.tierUuid;
          obj.isModifyTier = false;
        }
      });
    });
    setSelectedRows([]);
    setCurrentAppsRows([...currentAppsRows]);
  }

  const onChangeTier = (value) => {
    const tiersMap = [];
    selectedRows.forEach((id) => {
      const tier = tiersList.find( ({ tierUuid }) => tierUuid === value);
      const obj = { applicationUuid: id, tierUuid: value, tierName: tier.tierName };
      tiersMap.push(obj);
    })
    setSelectedTierArray(tiersMap);
    setSelectedTier(value);
  }

  const closeEulaDialog = () => setShowEulaDialog(false);

  const onSubmitEulaDialog = () => {
    const dataAccess = [];
    currentAppsRows.map((app)=> {
      if(app.isModify) {
        const obj = { applicationUuid: app.uuid, tierUuid: app.tierUuid };
        dataAccess.push(obj);
      }
    });
    if(dataAccess.length > 0) {
      props.updateProductApps(productUuid, dataAccess);
    }
    closeEulaDialog();
  }

  const noResultsMessage = filterByNameApplied ?
    `${intl.getI18nMessage('label.application.filter.no.results')}` :
    intl.getI18nMessage('label.application.no.results');

  const TierSelect = () => (
    <FormSelect
      {...props}
      handleChange={onSelectTier}
      id="product-details-apps-select-tier"
      data-apim-test="product-details-apps-select-tier"
      data={tiersList}
      value={selectedTierDialog}
      noNoneOption
      noNativeSelect
      fieldContainerClass={classes.selectContainer}
    />
  );
  const showAddBtn = changeMode === CHANGE_MODE_REMOVE_ACCESS;

  const onClickAdd = () => {
    clearDisable();
    setChangeMode(CHANGE_MODE_ADD_ACCESS);
    setShowFormActionBtns(true);
  };

  const getListColumns = () => [{
    id: 'name',
    label: getI18nFormattedMessage('label.application'),
    minWidth: 200,
    value: (item) => {
      return(
        !hasReadApp ?
          <span>{item.name}</span>
        :
          <Link underline="none" href={`/admin/console/applications/details/${item.uuid}`}>
            {item.name}
          </Link>
      )
    },
    }, {
    id: 'organizationName',
    label: getI18nFormattedMessage('label.organization'),
    minWidth: 200,
    value: (item) => {
      return(
      !hasReadOrg ?
        <span>{item.organizationName}</span>
      :
        <Link underline="none"
          href={`/admin/console/organizations/details/${item.organizationUuid}`}>
          {item.organizationName}
        </Link>
      )
    },
    isHidden : hasOrgUser,
    }, {
    id: 'status',
    label: getI18nFormattedMessage('label.state'),
    minWidth: 50,
    value: (item) => {
      const statusIconStyle = {
        fill: getApplicationStatusColor(item.status),
      };
      return (
        <span>
          <FiberManualRecordIcon size="0.82em"
              style={statusIconStyle} className={classes.statusIcon} />
            <span>{getApplicationStatusLabel(item.status)}</span>
        </span>);
      },
    }, {
      id: 'appProductAccessStatus',
      label: getI18nFormattedMessage('label.assign'),
      minWidth: 50,
      // eslint-disable-next-line react/prop-types
      format: ({ hasAccess, uuid, status }) =>
        (<Switch
          id={`${uuid}-access-toggle`}
          onChange={(val) => onAccessToggle(val, uuid)}
          checked={hasAccess}
          disabled={!hasUpdate || disabledAppStatusArray.includes(status)}
        />
      ),
      value: ({ uuid, appProductAccessStatus, status }) =>
          ({ uuid, hasAccess: appProductAccessStatus === ASSIGNED, status }),
    }, {
      id: 'tierName',
      label: (
        <ToolTipComp
          classes={classes}
          label={getI18nFormattedMessage('label.product.tiers.title')}
          info={<div>{getI18nFormattedMessage('label.apps.tier.tooltip')}</div>}
        />
      ),
      minWidth: 200,
      value: (item) => {
        return (
          (!hasUpdate || !hasUpdateTier || disabledAppStatusArray.includes(item.status)) ?
            <span id={`${item.uuid}-tier-name`}>{item.tierName}</span>
            :
            <Link className={classes.link} onClick={() => onClickTier(item.uuid)}
              underline="none">{item.tierName === USE_ORG_DEFAULT ? <i>{item.tierName}</i> : item.tierName}
            </Link>
        )
      },
    }];
  let appName = '';
  if(selectedRows.length > 0) {
    const index = findIndex(currentAppsRows, item => item.uuid === selectedRows[0]);
    appName = currentAppsRows[index].name;
  }
  const alertDialogTitle = intl.getI18nMessage(
    'label.application.details.apiproducts.alert.dialog.title',
    { name: productDetails ? productDetails.name : '' });
  const alertDialogDescription = intl.getI18nMessage(
    'label.application.details.products.alert.dialog.title',
    { name: appName })
  const emptyFunction = {};

  return (
    <>
      <AlertDialog
        description={alertDialogDescription}
        cancelText={'Cancel'}
        component={renderProdTiers()}
        dialogId={'app-tier-selection-dialog'}
        isOpen={showTierSelectionDialog}
        onClose={() => {}}
        onSubmit={emptyFunction}
        onCancel={setShowTierSelectionDialog.bind(null, false)}
        showSubmit={false}
        submitText={''}
        title={alertDialogTitle}
        assistText={intl.getI18nMessage(
          'label.application.details.apiproducts.tiers.list.assist.text')}
      />
      <AlertDialog
        description={alertDialogDescription}
        cancelText={'Cancel'}
        component={renderBulkTiers()}
        dialogId={'app-bulk-tier-selection-dialog'}
        isOpen={showBulkTier}
        onClose={() => {}}
        onSubmit={onAddAccessTier}
        onCancel={setShowBulkTier.bind(null, false)}
        showSubmit={true}
        submitText={intl.getI18nMessage('label.set.tier')}
        title={alertDialogTitle}
        assistText={intl.getI18nMessage(
          'label.application.details.apiproducts.tiers.list.assist.text')}
      />
      {isOpenTier &&
        <AlertDialog
          id="product-details-apps-tier-dialog"
          isOpen={isOpenTier}
          title={intl.getI18nMessage('label.select.tier')}
          component={<TierSelect />}
          submitText={intl.getI18nMessage('label.apply.tier')}
          cancelText={intl.getI18nMessage('label.cancel.button')}
          onClose={onCloseTier}
          onSubmit={onAddTierSubmit}
          onCancel={onCloseTier}
          containerClass={classes.dialogContainer}
          submitButtonClass={classes.submitButton}
          dialogActionClass={classes.dialogActionClass}
        />
      }
       <FormDialog
         isOpen={showEulaDialog}
         title={intl.getI18nMessage("label.application.details.apis.eulas.title")}
         submitText={intl.getI18nMessage("label.application.details.apis.eulas.acceptbutton")}
         cancelText={intl.getI18nMessage("label.cancel.button")}
         onClose={()=>{}}
         onSubmit={onSubmitEulaDialog}
         onCancel={closeEulaDialog}
         paperClass={classes.paperClass}
         handleClose={closeEulaDialog}
      >
        <EulaDialog
          eulaDetailsMap={eulaDetailsMap}
          eulaProductsMap={eulaProductMap}
          showCount={false}
        />
      </FormDialog>
      <ListContainer
        listPageId="product-details-apps-list"
        isLoading={isLoading}
        showListHeader={false}
        pageClass={classes.listContent}
        pageBodyClass={classes.pageBodyClass}
        pageFilterAndSortClass={classes.pageFilterAndSortClass}
        filterAndSortContent={(
          <Fragment>
            <FilterByName
              fieldContainerClass={classes.fieldContainer}
              name={intl.getI18nMessage('label.filter.by')}
              value={filterByName}
              placeholder={intl.getI18nMessage('label.application')}
              handleChange={onFilterByNameChange}
              onKeyPress={onFilterByNameKeyPress}
            />
            {!hasOrgUser &&
              <FilterByOrgs
                fieldContainerClass={classes.fieldContainer}
                name={intl.getI18nMessage('label.filter.by')}
                value={filterByOrg}
                placeholder={intl.getI18nMessage('label.organization')}
                handleChange={onFilterByOrgChange}
                onKeyPress={onFilterByOrgKeyPress}
                hideLabel
              />
            }
            <FilterByStatus
              fieldContainerClass={classes.fieldContainer}
              name={intl.getI18nMessage('label.filter')}
              value={filterByStatus}
              data={changeMode === CHANGE_MODE_ADD_ACCESS ? statusDataInChangeMode: statusData}
              handleChange={onFilterByStatusChange}
              hideLabel
              noNoneOption
              noNativeSelect
            />
            <FilterSeparator
              col={hasOrgUser ? 4 : 1}
            />
            {(showAddBtn && hasUpdate) &&
              <AddButton
                onClickAssign={onClickAdd}
                fieldContainerClass={classes.sortContainerClass}
                activeButton={classes.activeButton}
                disabled={modifiedRecords > 0}
              />
            }
          </Fragment>
        )}
        columns={getListColumns() || []}
        rows={currentAppsRows}
        bulkActionsContent={hasUpdate ?
          <HeaderActionButtons
            {...props}
            tiersList={tiersList}
            bulkActionsItemsCount={selectedRows.length}
            onAddAccessTier={onAddAccessTier}
            onRemoveAccess={onRemoveAccess}
            disableAddAccess={false}
            disableRemoveAccess={false}
            onChangeTier={onChangeTier}
            selectedTier={selectedTier}
            appsList={currentAppsRows}
            hasApplyTierLabel={changeMode === CHANGE_MODE_ADD_ACCESS}
            isRemoveAccess={changeMode === CHANGE_MODE_REMOVE_ACCESS}
            hasUpdateTier={hasUpdateTier}
            onBulkTierSelect={onBulkTierSelect}
            hasAddApp={changeMode === CHANGE_MODE_ADD_ACCESS}
            onRemoveAssign={onRemoveAssign}
            modifiedRecords={modifiedRecords}
            defaultText={USE_ORG_DEFAULT}
          />
          : ''
        }
        noResultsMessage={noResultsMessage}
        page={page}
        count={totalElements}
        totalElements={totalElements}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={GRID_ROWS_PER_PAGE_EXTRA_OPTIONS}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        selectedRowsObject={selectedRowsObject}
        setSelectedRowsObject={setSelectedRowsObject}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        onChangePreviousPage={onChangePreviousPage}
        onChangeNextPage={onChangeNextPage}
        hasPaginationDisabled={modifiedRecords > 0}
        paginationTooltipMessage={intl.getI18nMessage('label.apps.access.pagination.disabled.message')}
      />
      {showFormActionBtns &&
        <FormActionButtons
          isContainerFooter={true}
          onNextClick={onSave}
          onCancelClick={onCancel}
          nextText={intl.getI18nMessage('label.save.button')}
          cancelText={intl.getI18nMessage('label.cancel.button')}
          id="product-details-aps-tab-add-form-buttons"
          isDisabled={!isFormDirty}
          assistText={
          <span>
              {modifiedRecords}  {getI18nFormattedMessage('label.changed')}
              <WarningIcon className={classes.warningIcon} />
              {getI18nFormattedMessage('label.redepolyment.required')}
          </span>
          }
        />
      }
    </>
  )
}

Applications.propTypes = {
  classes: shape({}),
  productDetails: shape({}),
  isLoading: bool,
  totalPages: number,
  totalElements: number,
  rows: arrayOf(shape({})),
  fetchApplications: func,
  fetchTiersProduct: func,
  updateProductApps: func,
  productUuid: string,
  isCurrentTab: bool,
  isAppUpdateSuccess: bool,
  notifyMessages: func,
  push: func,
  userContext: shape({}),
  tiersListArray: arrayOf(shape({})),
  tabValue: string,
  eulaDetails: object,
  fetchApiEula: func,
};

const mapStateToProps = (state) => ({
  isLoading: false,
  rows: getAppsListResults(state),
  totalPages: getAppsListTotalPages(state),
  totalElements: getAppsListTotalElements(state),
  isAppUpdateSuccess: getIsAppUpdateSuccess(state),
  tiersListArray: getTiersProduct(state),
  productDetails: getproductDetails(state),
  eulaDetails: getApiEula(state),
  featureFlagDistributeRateLimit: getFeatureFlagForDistributeRateLimit(state),
});

const mapDispatchToProps = {
  fetchApplications,
  fetchTiersProduct,
  updateProductApps,
  fetchApiEula,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(Applications);
